import * as React from 'react';
import { Tooltip } from 'sos-common/js/components/Tooltip/Tooltip';
import { SelectBoxState } from 'sos-common/js/components/FormControls/SelectBox';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import { TextInput } from 'sos-common/js/components/FormControls/TextInput';
import { RadioButtonGroup } from 'sos-common/js/components/FormControls/RadioButtonGroup';
import { RadioButton } from 'sos-common/js/components/FormControls/RadioButton';
import { SelectBox, SelectBoxOption } from 'sos-common/js/components/FormControls/SelectBox';
import { MultiSelectComboboxFormControl } from 'sos-common/js/components/FormControls/MultiSelectComboboxFormControl';
import {
  SectionClass,
  SectionProps,
  SectionState,
  FieldName,
  OpenWebAnsokanInfoBubbles,
  FormType,
} from '../PublicForm.types';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';
import { FormField } from 'sos-common/js/commonTypes/formField.types';
import { MAX_LENGTH_NUM } from '../publicform.validation.service';
import { MultiSelectItem } from 'sos-common/js/commonTypes/multiSelect.types';

export interface IUtbildningSectionProps extends SectionProps {
  openWebAnsokanInfoBubbles: OpenWebAnsokanInfoBubbles;
  educatedInSweden?: boolean;
  countryList?: string[];
  outSideEuEes: boolean;
}

export interface IUtbildningSectionState extends SectionState {
  showOtherCountry: boolean;
  hasSelectedEducationForm: boolean;
  selectedEducationForm: string;
  selectedOtherCountries: string[];
}

enum UtbildningsForm {
  Vuxenutbildning = 'utbildningsform_vuxenutbildning',
  Gymnasium = 'utbildningsform_gymnasium',
  Annan = 'utbildningsform_annan',
  Sjukskoterskeutbildning = 'utbildningsform_sjukskoterskeutbildning',
}

const RequiredFieldListLegitimation = [
  FieldName.Utbildningsland,
  FieldName.Examenstitel,
  FieldName.Examensar,
  FieldName.Utbildningstid,
  FieldName.LegitimeradFran,
  FieldName.HarLegIAnnatLand,
];

const RequiredFieldListLegitimationSpecialistbevisOutsideEuEes = [
  FieldName.Examenstitel,
  FieldName.Examensar,
  FieldName.Utbildningstid,
  FieldName.LegitimeradFran,
  FieldName.HarLegIAnnatLand,
];

const RequiredFieldListEducatedOutsideOfSweden = [
  FieldName.Utbildningsland,
  FieldName.Examenstitel,
  FieldName.Examensar,
  FieldName.Utbildningstid,
  FieldName.HarLegIAnnatLand,
];

const RequiredFieldListEducatedInSweden = [FieldName.Utbildningsform, FieldName.Utbildningsperiod];

export default class UtbildningSection extends SectionClass<IUtbildningSectionProps, IUtbildningSectionState> {
  private _mandatoryString: string;
  private _educatedOutsideEuEesTextSwe = 'Utanför EU/EES';
  private _educatedOutsideEuEesTextEn = 'Outside EU/EES';
  constructor(props: IUtbildningSectionProps) {
    super(props);
    this._mandatoryString = ' ' + props.Translate('mandatory');
    this.state = {
      selectedEducationForm: null,
      hasSelectedEducationForm: false,
      showOtherCountry: false,
      selectedOtherCountries: [],
      errors:
        props.formType === FormType.Legitimation
          ? this.props.outSideEuEes
            ? this.getInitialErrorMap(RequiredFieldListLegitimationSpecialistbevisOutsideEuEes)
            : this.getInitialErrorMap(RequiredFieldListLegitimation)
          : props.educatedInSweden
          ? this.getInitialErrorMap(RequiredFieldListEducatedInSweden)
          : this.getInitialErrorMap(RequiredFieldListEducatedOutsideOfSweden),
    };
  }

  static defaultProps = {
    formLanguage: 'sv',
  } as IUtbildningSectionProps;

  onChangeEducationForm = (field: FormField<string>) => {
    const { Translate } = this.props;
    const errors = new Map(this.state.errors);

    const hasSelectedEducationForm = field.value ? true : false;
    const selectedEducationForm = field.value;
    if (selectedEducationForm === UtbildningsForm.Sjukskoterskeutbildning) {
      errors.delete(FieldName.Utbildningsperiod);
    }
    this.setState({ hasSelectedEducationForm, selectedEducationForm, errors }, () => {
      this.onChangeField(field);
    });
  };

  SelectOtherCountry = (MultiSelectItem: MultiSelectItem[]) => {
    const data = {
      isRequired: true,
      value: MultiSelectItem.map((x) => x.value),
      isTouched: true,
      selectedText: FieldName.AngeLandController,
      fieldName: FieldName.AngeLandController,
    };

    this.setState({ selectedOtherCountries: MultiSelectItem.map((x) => x.value) }, () => this.onChangeField(data));
  };

  onChangeOtherCountry = (field: FormField<string>) => {
    const { Translate } = this.props;
    const errors = new Map(this.state.errors);
    const showOtherCountry = field.value === Translate('Ja');

    if (showOtherCountry) {
      errors.set(FieldName.AngeLandController, { showMessage: false, errorMessage: Translate('error_mandatory') });
    } else {
      errors.delete(FieldName.AngeLandController);
    }

    this.setState({ showOtherCountry, errors }, () => {
      this.onChangeField(field);
      if (showOtherCountry) {
        const angelandElem = document.getElementById(FieldName.AngeLandController);
        angelandElem?.focus();
      }
    });
  };

  get countrySelectBoxOption(): SelectBoxOption[] {
    return this.props.countryList.map((country) => {
      return { key: country, text: country };
    });
  }

  get educationFormSelectBoxOption(): SelectBoxOption[] {
    const { Translate } = this.props;
    var options = [];
    options.push({ key: UtbildningsForm.Gymnasium, text: Translate(UtbildningsForm.Gymnasium) });
    options.push({ key: UtbildningsForm.Vuxenutbildning, text: Translate(UtbildningsForm.Vuxenutbildning) });
    options.push({ key: UtbildningsForm.Annan, text: Translate(UtbildningsForm.Annan) });
    options.push({
      key: UtbildningsForm.Sjukskoterskeutbildning,
      text: Translate(UtbildningsForm.Sjukskoterskeutbildning),
    });
    return options;
  }

  public render() {
    const {
      Translate,
      openWebAnsokanEpiTexts,
      isInEditMode,
      openWebAnsokanInfoBubbles,
      formLanguage,
      skyddadYrkestitelOpenFormEpiTexts,
      skyddadYrkestitelOpenFormInfoBubbles,
      formType,
      educatedInSweden,
    } = this.props;
    const { showOtherCountry, hasSelectedEducationForm, selectedEducationForm } = this.state;
    return (
      <section aria-label={`${Translate('section_utbildning')}`}>
        <EpiserverProperty propertyName="UtbildningHeading" isInEditMode={isInEditMode}>
          <h2>
            {formType === FormType.SkyddadYrkestitel
              ? skyddadYrkestitelOpenFormEpiTexts.UtbildningHeading
              : openWebAnsokanEpiTexts.UtbildningHeading}
          </h2>
        </EpiserverProperty>

        <EpiserverProperty propertyName="UtbildningPreamble" wrappingElement="span" isInEditMode={isInEditMode}>
          <p className="preamble">
            {formType === FormType.SkyddadYrkestitel
              ? skyddadYrkestitelOpenFormEpiTexts.UtbildningPreamble
              : openWebAnsokanEpiTexts.UtbildningPreamble}
          </p>
        </EpiserverProperty>

        <EpiserverProperty
          propertyName="UtbildningBody"
          usingDangerouslySetInnerHTML={true}
          isInEditMode={isInEditMode}>
          <div
            dangerouslySetInnerHTML={setInnerHtml(
              formType === FormType.SkyddadYrkestitel
                ? skyddadYrkestitelOpenFormEpiTexts.UtbildningBody
                : openWebAnsokanEpiTexts.UtbildningBody,
            )}
          />
        </EpiserverProperty>

        {formType === FormType.SkyddadYrkestitel && educatedInSweden && (
          <div>
            <div className="row">
              <div className="col-sm-6 flex-wrap select-full-width">
                <SelectBox
                  onChange={this.onChangeEducationForm}
                  label={Translate(FieldName.Utbildningsform) + this._mandatoryString}
                  defaultOptionText={Translate('valjutbildningsform')}
                  fieldName={FieldName.Utbildningsform}
                  options={this.educationFormSelectBoxOption}
                  errorMessage={this.getErrorMessage(FieldName.Utbildningsform)}
                  isRequired={true}
                />
                {hasSelectedEducationForm && (
                  <div className="like-a-label like-a-label--wrap">
                    {selectedEducationForm === UtbildningsForm.Gymnasium && (
                      <RadioButtonGroup
                        onChange={this.onChangeField}
                        errorMessage={this.getErrorMessage(FieldName.Utbildningsperiod)}
                        isRequired={true}
                        fieldName={FieldName.Utbildningsperiod}
                        label={Translate(FieldName.Utbildningsperiod) + this._mandatoryString}
                        ariaLabel={FieldName.Utbildningsperiod.toString()}
                        subLabel={Translate('utbildningsperiod_sublabel')}>
                        <RadioButton value={'innan1994'} label={Translate('innan1994')} />
                        <RadioButton value={'mellan1994och1999'} label={Translate('mellan1994och1999')} />
                        <RadioButton value={'mellan2000och2010'} label={Translate('mellan2000och2010')} />
                        <RadioButton value={'mellan2011och2020'} label={Translate('mellan2011och2020')} />
                        <RadioButton value={'fran2021'} label={Translate('fran2021')} />
                        <RadioButton value={'efter2023'} label={Translate('efter2023')} />
                      </RadioButtonGroup>
                    )}
                    {selectedEducationForm === UtbildningsForm.Vuxenutbildning && (
                      <RadioButtonGroup
                        onChange={this.onChangeField}
                        errorMessage={this.getErrorMessage(FieldName.Utbildningsperiod)}
                        isRequired={true}
                        fieldName={FieldName.Utbildningsperiod}
                        label={Translate(FieldName.Utbildningsperiod) + this._mandatoryString}
                        ariaLabel={FieldName.Utbildningsperiod.toString()}
                        subLabel={Translate('utbildningsperiod_sublabel')}>
                        <RadioButton value={'innan1994'} label={Translate('innan1994')} />
                        <RadioButton value={'mellan1994och1999'} label={Translate('mellan1994och1999')} />
                        <RadioButton value={'mellan2000ochvt2012'} label={Translate('mellan2000ochvt2012')} />
                        <RadioButton value={'mellanht2012ochvt2018'} label={Translate('mellanht2012ochvt2018')} />
                        <RadioButton value={'mellanht2018ochvt2021'} label={Translate('mellanht2018ochvt2021')} />
                        <RadioButton value={'franht2021'} label={Translate('franht2021')} />
                        <RadioButton value={'efter2023'} label={Translate('efter2023')} />
                      </RadioButtonGroup>
                    )}
                    {selectedEducationForm === UtbildningsForm.Annan && (
                      <RadioButtonGroup
                        onChange={this.onChangeField}
                        errorMessage={this.getErrorMessage(FieldName.Utbildningsperiod)}
                        isRequired={true}
                        fieldName={FieldName.Utbildningsperiod}
                        label={Translate(FieldName.Utbildningsperiod) + this._mandatoryString}
                        ariaLabel={FieldName.Utbildningsperiod.toString()}
                        subLabel={Translate('utbildningsperiod_sublabel')}>
                        <RadioButton value={'fore2023'} label={Translate('fore2023')} />
                        <RadioButton value={'efter2023'} label={Translate('efter2023')} />
                      </RadioButtonGroup>
                    )}
                  </div>
                )}
              </div>

              {selectedEducationForm === UtbildningsForm.Sjukskoterskeutbildning && (
                <div className="importantinfo importantinfo--lightgray importantinfo--sharp">
                  <p>{Translate('sjukskoterskaInformationText')}</p>
                </div>
              )}
            </div>
          </div>
        )}
        {(formType === FormType.Legitimation || !educatedInSweden) && (
          <div>
            <div className="row m-b-2">
              {!this.props.outSideEuEes ? (
                <div
                  className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.Examenstitel, [
                    FieldName.Utbildningsland,
                  ])}`}>
                  <SelectBox
                    onChange={this.onChangeField}
                    label={Translate(FieldName.Utbildningsland) + this._mandatoryString}
                    defaultOptionText={Translate('valjutbildningsland')}
                    fieldName={FieldName.Utbildningsland}
                    options={
                      !educatedInSweden && formType === FormType.SkyddadYrkestitel
                        ? this.countrySelectBoxOption
                        : this.countrySelectBoxOption.filter(
                            (x) =>
                              x.key !== this._educatedOutsideEuEesTextSwe && x.key !== this._educatedOutsideEuEesTextEn,
                          )
                    }
                    errorMessage={this.getErrorMessage(FieldName.Utbildningsland)}
                    isRequired={true}
                  />
                </div>
              ) : (
                <input
                  type="hidden"
                  name={FieldName.Utbildningsland}
                  value={
                    this.props.formLanguage === 'sv'
                      ? this._educatedOutsideEuEesTextSwe
                      : this._educatedOutsideEuEesTextEn
                  }
                />
              )}
              <div
                className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.Utbildningsland, [
                  FieldName.Examenstitel,
                ])}`}>
                <TextInput
                  onChange={this.onChangeField}
                  errorMessage={this.getErrorMessage(FieldName.Examenstitel)}
                  label={Translate(FieldName.Examenstitel) + this._mandatoryString}
                  isRequired={true}
                  fieldName={FieldName.Examenstitel}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={formLanguage}
                />
              </div>
            </div>
            <div className="row m-b-2">
              <div className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.Utbildningstid, [FieldName.Examensar])}`}>
                <TextInput
                  onChange={this.onChangeField}
                  errorMessage={this.getErrorMessage(FieldName.Examensar)}
                  label={Translate(FieldName.Examensar) + this._mandatoryString}
                  isRequired={true}
                  fieldName={FieldName.Examensar}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={formLanguage}
                />
              </div>
              <div className={`col-sm-6 ${this.getErrorSiblingClass(FieldName.Examensar, [FieldName.Utbildningstid])}`}>
                <TextInput
                  onChange={this.onChangeField}
                  errorMessage={this.getErrorMessage(FieldName.Utbildningstid)}
                  label={Translate(FieldName.Utbildningstid) + this._mandatoryString}
                  isRequired={true}
                  fieldName={FieldName.Utbildningstid}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={formLanguage}
                />
              </div>
            </div>
            <div className="row m-b-2">
              <div className="col-sm-6">
                {formType === FormType.SkyddadYrkestitel &&
                  skyddadYrkestitelOpenFormInfoBubbles.bubble_legitimeradfran && (
                    <Tooltip
                      text={skyddadYrkestitelOpenFormInfoBubbles.bubble_legitimeradfran}
                      editableEpiserverPropertyName={'bubble_legitimeradfran'}
                      enableEpiserverOnPageEditing={true}
                      isInEditMode={isInEditMode}
                    />
                  )}
                {formType === FormType.Legitimation && openWebAnsokanInfoBubbles?.bubble_legitimeradfran && (
                  <Tooltip
                    text={openWebAnsokanInfoBubbles.bubble_legitimeradfran}
                    editableEpiserverPropertyName={'bubble_legitimeradfran'}
                    enableEpiserverOnPageEditing={true}
                    isInEditMode={isInEditMode}
                  />
                )}
                <TextInput
                  onChange={this.onChangeField}
                  errorMessage={this.getErrorMessage(FieldName.LegitimeradFran)}
                  label={
                    formType === FormType.Legitimation
                      ? Translate(FieldName.LegitimeradFran) + this._mandatoryString
                      : Translate(FieldName.LegitimeradFran)
                  }
                  isRequired={formType === FormType.Legitimation}
                  fieldName={FieldName.LegitimeradFran}
                  maxLength={MAX_LENGTH_NUM}
                  showMaxLengthMessage={true}
                  maxLengthMessageLanguage={formLanguage}
                />
              </div>
            </div>

            <RadioButtonGroup
              errorMessage={this.getErrorMessage(FieldName.HarLegIAnnatLand)}
              onChange={this.onChangeOtherCountry}
              isRequired={true}
              fieldName={FieldName.HarLegIAnnatLand}
              label={Translate(FieldName.HarLegIAnnatLand) + this._mandatoryString}
              ariaLabel={FieldName.HarLegIAnnatLand.toString()}>
              <RadioButton value={Translate('Ja')} label={Translate('Ja')} />
              <RadioButton value={Translate('Nej')} label={Translate('Nej')} />
            </RadioButtonGroup>

            {showOtherCountry && (
              <div className="row">
                <div className="col-sm-6">
                  <MultiSelectComboboxFormControl
                    items={this.countrySelectBoxOption.map((x) => {
                      return { key: x.key, value: x.text, isSelectedByDefault: false } as MultiSelectItem;
                    })}
                    enableShowAllOption={false}
                    textBoxLabelText={Translate('valjlandLander')}
                    showRemoveSelectionsButton={false}
                    onSelectedItemsChange={this.SelectOtherCountry}
                    fieldName={FieldName.AngeLandController}
                    id={FieldName.AngeLandController}
                    isRequired={true}
                    errorMessage={this.getErrorMessage(FieldName.AngeLandController)}
                    showErrorMessageWithoutTouched={this.props.submitButtonIsClicked}
                  />
                  <input
                    type="hidden"
                    name={FieldName.AngeLand}
                    value={this.state.selectedOtherCountries ? this.state.selectedOtherCountries.join(',') : ''}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        <hr className="formdivider" />
      </section>
    );
  }
}
